import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import {
  PaxApi,
  OrderDTO,
  PaymentDTO,
  TerminalDTO,
  PaymentMethodPaymentMethodIdEnum,
  PaymentDTOTransactionTypeEnum,
} from '@lkp/parking-sdk';
import { Subject, takeUntil } from 'rxjs';
import {
  CancelCheckout,
  CheckoutOrderPax,
  GetOrder,
  Refund,
  ResetOrderTransState,
  SaveTerminal,
} from 'src/app/core/states/orders/orders.action';
import { OrdersState } from 'src/app/core/states/orders/orders.state';
import { PAX_API } from 'src/app/core/providers/api.module';
@Component({
    selector: 'app-pax-payment',
    templateUrl: './pax-payment.component.html',
    styleUrls: ['./pax-payment.component.scss'],
    standalone: false
})
export class PaxPaymentComponent implements OnInit, OnDestroy {
  paymentMethod: 'card' | 'bank' = 'card';
  refund = false;
  loading = false;

  availTerminals: TerminalDTO[] = [];
  terminalsFetched = false;

  transactionStarted = false;
  showChooseTerminal = false;
  transactionFinished = false;
  printOffered = false;
  selectedTerminal?: TerminalDTO = undefined;

  declined = false;

  dataCopy: any;

  refundAmt?: number;
  refundControl = new FormControl<number | undefined>(this.data.maxRefund, [
    Validators.max(this.data.maxRefund),
    Validators.min(0),
  ]);
  commentControl = new FormControl(undefined, Validators.required);
  paymentControl = new FormControl<number | undefined>(
    this.data.order.amountToPay,
    [
      Validators.max(this.data.order.amountToPay!),
      Validators.min(0),
      Validators.required,
    ]
  );

  form = new FormControl<TerminalDTO | undefined>(undefined);

  readonly onDestroy$ = new Subject<void>();

  constructor(
    readonly store: Store,
    public dialogRef: MatDialogRef<PaxPaymentComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      order: OrderDTO;
      refund: PaymentDTO;
      maxRefund: number;
      orderItemId: number;
    },
    @Inject(PAX_API) readonly paxApi: PaxApi,
    private cdr: ChangeDetectorRef
  ) {
    dialogRef.disableClose = true;

    this.dataCopy = {
      order: data.order ? { ...data.order } : undefined,
      refund: data.refund ? { ...data.refund } : undefined,
      maxRefund: data.maxRefund,
      orderItemId: data.orderItemId,
    };
    this.refund = !!this.dataCopy.refund;
  }

  ngOnInit(): void {
    this.store.dispatch(new ResetOrderTransState());
    this.findTerminals();
  }

  findTerminals() {
    this.paxApi
      .paxFindTerminalsAdmin()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((terminals) => {
        this.availTerminals = terminals;
        this.store
          .select(OrdersState.terminal)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((terminal) => {
            setTimeout(() => {
              if (terminal) {
                this.form.setValue(terminal);
                this.selectedTerminal = terminal;
                this.terminalsFetched = true;
              }
              this.cdr.markForCheck(); // Needed to solve angular change detection issue
            });
          });
      });
  }

  getTitle(): string {
    if (this.showChooseTerminal) {
      return 'Välj betalterminal';
    } else if (!this.showChooseTerminal && this.dataCopy.refund) {
      return 'Återköp';
    } else if (
      !this.transactionStarted &&
      !this.transactionFinished &&
      !this.showChooseTerminal
    ) {
      return 'Belopp';
    } else {
      return 'Betalning';
    }
  }

  showCancelBtn(): boolean {
    return this.transactionStarted && !this.transactionFinished;
  }

  /* TODO: better type */
  finalStatus(e: any) {
    if (e.cancelled) {
      this.transactionStarted = false;
      this.dialogRef.close({ cancelled: true });
    } else if (e.approved) {
      this.transactionFinished = true;
    } else if (e.declined) {
      this.declined = true;
    } else if (e.ended) {
      this.transactionStarted = false;
    } else if (e.noConnection) {
      this.transactionStarted = false;
      this.store.dispatch(new ResetOrderTransState());
      this.dialogRef.close({ unavailable: true });
    }
  }

  changeRefundMethod(value: 'card' | 'bank') {
    this.paymentMethod = value;
  }

  chooseTerminal() {
    this.showChooseTerminal = true;
  }

  bankRefund() {
    let payment: PaymentDTO = {
      paymentMethod: { paymentMethodId: PaymentMethodPaymentMethodIdEnum.Bank },
      transactionType: PaymentDTOTransactionTypeEnum.Refund,
      amount: this.refundControl.value!,
      comment: this.commentControl.value!,
    };

    this.store
      .dispatch(
        new Refund(
          this.dataCopy.order.orderId!,
          this.dataCopy.orderItemId,
          this.dataCopy.refund.paymentId!,
          payment
        )
      )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => (this.transactionFinished = true),
        error: (error) =>
          console.error(
            'Something went wrong with bank refund. Error: ',
            error
          ),
      });
  }

  startPayment(terminal: TerminalDTO) {
    this.transactionStarted = true;
    this.transactionFinished = false;
    this.showChooseTerminal = false;
    this.loading = true;
    this.store.dispatch(new SaveTerminal(terminal));

    // Payment
    if (!this.dataCopy.refund) {
      // Create PaymentDTO
      let payment: PaymentDTO = {
        paymentMethod: {
          paymentMethodId: PaymentMethodPaymentMethodIdEnum.Pax,
        },
        transactionType: PaymentDTOTransactionTypeEnum.Payment,
        terminalId: terminal.terminalId,
        amount: this.paymentControl.value!,
      };

      this.store
        .dispatch(new CheckoutOrderPax(this.dataCopy.order.orderId!, payment))
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: () => {
            this.loading = false;
          },
          error: (error) => {
            console.error('Error when initiating card payment: ', error);
            this.loading = false;
          },
        });
    } else {
      // Refund
      let payment: PaymentDTO = {
        paymentMethod: this.dataCopy.refund.paymentMethod,
        transactionType: PaymentDTOTransactionTypeEnum.Refund,
        amount: this.refundControl.value!,
        terminalId: terminal.terminalId,
        comment: this.commentControl.value!,
      };

      this.store
        .dispatch(
          new Refund(
            this.dataCopy.order.orderId!,
            this.dataCopy.orderItemId,
            this.dataCopy.refund.paymentId!,
            payment
          )
        )
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.loading = false;
          this.dataCopy.order.orderId &&
            this.store.dispatch(new GetOrder(this.dataCopy.order.orderId));
        });
    }
  }

  receiptPrint() {
    this.printOffered = true;
    this.dialogRef.close({
      accepted: true,
      success: true,
      receipt: true,
      paidAmt: this.paymentControl.value!,
    });
  }

  skipPrint() {
    this.printOffered = true;
    this.dialogRef.close({
      accepted: true,
      success: true,
      paidAmt: this.paymentControl.value!,
    });
  }

  cancelPayment(terminalId: number) {
    if (this.declined) {
      this.dialogRef.close({ cancelled: true });
    } else
      this.store
        .dispatch(new CancelCheckout(terminalId))
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => this.closeDialog());
  }

  closeDialog() {
    this.dialogRef.close({ cancelled: true });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
